import React from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
  type ColumnDef
} from "@tanstack/react-table";
import { MoreHorizontal, PlusCircle, Star, Eye, ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Checkbox } from '@/components/ui/checkbox';

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { StockRawRecord } from "./types";

// Common cell content styles
const cellTextStyles = "text-sm w-full whitespace-normal break-words hyphens-auto";
const codeTextStyles = "text-xs font-mono whitespace-normal break-all hyphens-auto overflow-visible";
const naTextStyles = "text-muted-foreground italic text-sm";

// Reusable header component with sort indicators
const SortableHeader = ({ column, title }: { column: any; title: string }) => {
  const isSorted = column.getIsSorted();
  return (
    <div className="flex items-center gap-1 group select-none">
      <span className="text-xs font-medium">{title}</span>
      {column.getCanSort() && (
        <span className="opacity-70 group-hover:opacity-100">
          {isSorted === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5" />
          ) : isSorted === 'desc' ? (
            <ArrowDown className="h-3.5 w-3.5" />
          ) : (
            <ArrowUpDown className="h-3.5 w-3.5 opacity-40 group-hover:opacity-70" />
          )}
        </span>
      )}
    </div>
  );
};

// Format market cap from string to readable format
const formatMarketCap = (value: string | undefined): string => {
  if (!value) return 'N/A';
  
  // Parse the string to a number, handle possible formatting
  const numValue = parseFloat(value.replace(/[^\d.-]/g, ''));
  if (isNaN(numValue)) return 'N/A';
  
  if (numValue >= 1_000_000_000_000) {
    return `$${(numValue / 1_000_000_000_000).toFixed(1)}T`;
  }
  if (numValue >= 1_000_000_000) {
    return `$${(numValue / 1_000_000_000).toFixed(1)}B`;
  }
  if (numValue >= 1_000_000) {
    return `$${(numValue / 1_000_000).toFixed(1)}M`;
  }
  
  return `$${numValue.toLocaleString()}`;
};

interface ResizableStocksTableProps {
  data: StockRawRecord[];
  isLoading: boolean;
  onAddToPortfolio: (stockId: string, stock?: StockRawRecord) => void;
  onFavorite: (stockId: string) => void;
  onAddToWatchlist?: (stockId: string) => void;
  bulkSelection?: {
    isSelected: (id: string) => boolean;
    toggleItem: (id: string) => void;
    toggleAll: (allIds: string[]) => void;
    isAllSelected: boolean;
    isIndeterminate: boolean;
  };
}

export function ResizableStocksTable({
  data,
  isLoading,
  onAddToPortfolio,
  onFavorite,
  onAddToWatchlist,
  bulkSelection,
}: ResizableStocksTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  
  const columns = React.useMemo<ColumnDef<StockRawRecord>[]>(() => {
    const cols: ColumnDef<StockRawRecord>[] = [];
    
    // Add checkbox column if bulk selection is enabled
    if (bulkSelection) {
      const currentPageIds = data.map(stock => stock.id || stock.code);
      cols.push({
        id: 'select',
        header: () => (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={bulkSelection.isAllSelected}
              ref={(el) => {
                if (el) {
                  const input = el.querySelector('input');
                  if (input) input.indeterminate = bulkSelection.isIndeterminate;
                }
              }}
              onCheckedChange={() => bulkSelection.toggleAll(currentPageIds)}
              aria-label="Select all"
              className="h-3.5 w-3.5"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={bulkSelection.isSelected(row.original.id || row.original.code)}
              onCheckedChange={() => bulkSelection.toggleItem(row.original.id || row.original.code)}
              aria-label="Select row"
              className="h-3.5 w-3.5"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ),
        size: 40,
        minSize: 40,
        maxSize: 40,
        enableResizing: false,
        enableSorting: false,
        meta: {
          className: 'px-2 py-1 text-center'
        }
      });
    }
    
    cols.push(
    {
      accessorKey: 'code',
      header: ({ column }) => <SortableHeader column={column} title="Ticker" />,
      size: 100,
      minSize: 80,
      maxSize: 120,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'pl-4 pr-2 py-2 align-top'
      },
      cell: info => <span className={codeTextStyles}>{String(info.getValue() ?? '')}</span>,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} title="Name" />,
      size: 250,
      minSize: 150,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'px-4 py-2 align-top'
      },
      cell: info => <span className={cellTextStyles}>{String(info.getValue() ?? '-')}</span>,
    },
    {
      accessorKey: 'exchange_name',
      header: ({ column }) => <SortableHeader column={column} title="Exchange" />,
      size: 150,
      minSize: 120,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'px-4 py-2 align-top'
      },
      cell: info => <span className={cellTextStyles}>{String(info.getValue() ?? '-')}</span>,
    },
    {
      accessorKey: 'marketcap',
      header: ({ column }) => <SortableHeader column={column} title="Market Cap" />,
      size: 120,
      minSize: 100,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'px-4 py-2 align-top'
      },
      cell: ({ row }) => {
        const value = formatMarketCap(row.original.marketcap);
        if (value === 'N/A') {
          return <span className={naTextStyles}>N/A</span>;
        }
        return <span className={cellTextStyles}>{value}</span>;
      },
    },
    {
      accessorKey: 'gicgroup',
      header: ({ column }) => <SortableHeader column={column} title="Sector" />,
      size: 150,
      minSize: 120,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'px-4 py-2 align-top'
      },
      cell: ({ row }) => {
        const value = row.original.gicgroup;
        if (!value) {
          return <span className={naTextStyles}>N/A</span>;
        }
        return <span className={cellTextStyles}>{value}</span>;
      },
    },
    {
      id: 'actions',
      header: () => <div className="text-right text-xs font-medium"></div>,
      size: 60,
      minSize: 60,
      maxSize: 80,
      meta: {
        className: 'pl-2 pr-4 py-2 text-right align-top'
      },
      cell: ({ row }: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAddToPortfolio(row.original.id, row.original)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add to Portfolio
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onFavorite(row.original.id)}>
              <Star className="mr-2 h-4 w-4" />
              Favorite
            </DropdownMenuItem>
            {onAddToWatchlist && (
              <DropdownMenuItem onClick={() => onAddToWatchlist(row.original.id)}>
                <Eye className="mr-2 h-4 w-4" />
                Add to Watchlist
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    });
    
    return cols;
  }, [onAddToPortfolio, onFavorite, onAddToWatchlist, bulkSelection, data]);
  
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  
  return (
    <div className="w-full rounded-md border">
      <div className="w-full overflow-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      "text-left sticky z-10 bg-muted/10 h-8 px-3 py-1",
                      "text-xs font-medium text-muted-foreground",
                      header.column.getCanSort() ? "select-none cursor-pointer transition-colors hover:bg-muted/40" : ""
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            <AnimatePresence key={`stocks-${data.length}-${data[0]?.id || 'empty'}`}>
              {table.getRowModel().rows.map((row, index) => (
              <motion.tr
                key={row.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
                className={cn(
                  "hover:bg-muted/20 transition-colors",
                  index % 2 === 0 ? "bg-background" : "bg-muted/40"
                )}
              >
                {row.getVisibleCells().map(cell => (
                  <TableCell 
                    key={cell.id} 
                    className="px-4 py-2 transition-colors align-top"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </motion.tr>
              ))}
            </AnimatePresence>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ResizableStocksTable;
